import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, IconButton, Tooltip,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { menuProps, dialogPaperProps } from '../styles/formStyles';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import TurnoDialog from '../components/TurnoDialog/TurnoDialog';

const API = process.env.REACT_APP_API_URL || '';

const ESTADOS = [
  { value: 'pendiente',    label: 'Pendiente',    color: '#1565c0', bg: 'rgba(21,101,192,0.85)'  },
  { value: 'confirmado',   label: 'Confirmado',   color: '#2e7d32', bg: 'rgba(46,125,50,0.85)'   },
  { value: 'en_recepcion', label: 'En recepción', color: '#6a1b9a', bg: 'rgba(106,27,154,0.85)'  },
  { value: 'demorado',     label: 'Demorado',     color: '#e65100', bg: 'rgba(230,81,0,0.85)'    },
  { value: 'atendido',     label: 'Atendido',     color: '#37474f', bg: 'rgba(55,71,79,0.85)'    },
  { value: 'cancelado',    label: 'Cancelado',    color: '#b71c1c', bg: 'rgba(183,28,28,0.7)'    },
];


// Función, no constante: si quedara fijada al cargar el módulo, una pestaña que sigue abierta
// de un día para el otro (recepción/consultorio no suelen cerrar el navegador) se queda mostrando
// "hoy" congelado en el día en que se cargó la página por última vez.
function hoy() { return new Date().toISOString().split('T')[0]; }

function sumarDias(fecha, n) {
  const d = new Date(fecha + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

function formatFechaLarga(fecha) {
  return new Date(fecha + 'T12:00:00').toLocaleDateString('es-AR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

function estadoInfo(estado) {
  return ESTADOS.find(e => e.value === estado) || ESTADOS[0];
}

// ── Componentes del mini-calendario ─────────────────────────────────────────
const DIAS_SEM_CAL = ['Lu','Ma','Mi','Ju','Vi','Sa','Do'];
const DIAS_MAP_CAL = { 0:'Domingo',1:'Lunes',2:'Martes',3:'Miercoles',4:'Jueves',5:'Viernes',6:'Sabado' };
const MESES_CAL = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function MiniCalendarioPicker({ mes, onMesChange, ocupacion, extraordinaria, horarios, fechaSeleccionada, onFechaClick }) {
  const [anio, mesNum] = mes.split('-').map(Number);
  const diasEnMes = new Date(anio, mesNum, 0).getDate();
  let offsetLunes = new Date(anio, mesNum - 1, 1).getDay();
  offsetLunes = offsetLunes === 0 ? 6 : offsetLunes - 1;
  const celdas = [];
  for (let i = 0; i < offsetLunes; i++) celdas.push(null);
  for (let d = 1; d <= diasEnMes; d++) celdas.push(d);

  const navMes = (delta) => {
    const d = new Date(anio, mesNum - 1 + delta, 1);
    onMesChange(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };

  function tieneHorario(dia) {
    if (!horarios?.length) return false;
    const fecha = `${anio}-${String(mesNum).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
    const diaNom = DIAS_MAP_CAL[new Date(fecha+'T12:00:00').getDay()];
    return horarios.some(h => h.dia === diaNom);
  }

  function getDiaInfo(dia) {
    const fecha = `${anio}-${String(mesNum).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
    const ocp = ocupacion[fecha];
    if (ocp === 'ausente') return 'ausente';
    // Disponibilidad extraordinaria reemplaza la agenda habitual — ese día
    // cuenta como "con horario" aunque el día de la semana no tenga agenda.
    const tieneExtra = extraordinaria?.[fecha]?.length > 0;
    if (!tieneExtra && !tieneHorario(dia)) return 'sin_horario';
    if (typeof ocp === 'number' && ocp > 0) return 'con_turnos';
    return 'libre';
  }

  return (
    <Box sx={{ userSelect: 'none' }}>
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1.5 }}>
        <IconButton size="small" onClick={() => navMes(-1)} sx={{ color:'var(--c-acento)', p:0.5 }}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontSize:14, fontWeight:700, color:'var(--c-acento)', textTransform:'capitalize' }}>
          {MESES_CAL[mesNum-1]} {anio}
        </Typography>
        <IconButton size="small" onClick={() => navMes(1)} sx={{ color:'var(--c-acento)', p:0.5 }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'3px', mb:0.5 }}>
        {DIAS_SEM_CAL.map(d => (
          <Box key={d} sx={{ textAlign:'center', fontSize:10, color:'var(--c-acento)', fontWeight:700, opacity:0.55, pb:0.5 }}>{d}</Box>
        ))}
      </Box>
      <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'3px' }}>
        {celdas.map((dia, i) => {
          if (!dia) return <Box key={`e${i}`} />;
          const fecha = `${anio}-${String(mesNum).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
          const tipo = getDiaInfo(dia);
          const sel = fecha === fechaSeleccionada;
          const ESTILOS = {
            libre:       { bg: sel ? 'var(--c-acento)' : 'rgba(102,187,106,0.15)', txt: sel ? '#000' : '#81c784', bdr: sel ? 'var(--c-acento)' : '#66bb6a55' },
            con_turnos:  { bg: sel ? '#ff9800' : 'rgba(255,152,0,0.15)',   txt: sel ? '#000' : '#ffb74d', bdr: sel ? '#ff9800' : '#ff980055' },
            ausente:     { bg: sel ? '#ef5350' : 'rgba(244,67,54,0.14)',   txt: sel ? '#fff' : '#ef9a9a', bdr: sel ? '#ef5350' : '#ef535055' },
            sin_horario: { bg: sel ? '#607d8b' : 'rgba(96,125,139,0.08)',  txt: sel ? '#fff' : '#546e7a', bdr: sel ? '#607d8b' : 'rgba(96,125,139,0.2)' },
          };
          const est = ESTILOS[tipo] || ESTILOS.libre;
          const tip = { ausente:'Ausencia', sin_horario:'Sin agenda', con_turnos:'Con turnos', libre:'Disponible' }[tipo];
          return (
            <Tooltip key={dia} title={tip} placement="top" arrow>
              <Box onClick={() => onFechaClick(fecha)} sx={{
                display:'flex', alignItems:'center', justifyContent:'center',
                height:34, borderRadius:1.5, cursor:'pointer',
                bgcolor:est.bg, color:est.txt, border:`1px solid ${est.bdr}`,
                fontSize:12, fontWeight: sel ? 800 : 400,
                transition:'all 0.12s ease',
                '&:hover': { filter:'brightness(1.25)', transform:'scale(1.07)' },
              }}>
                {dia}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display:'flex', gap:2, mt:1.5, justifyContent:'center' }}>
        {[['#66bb6a','Disponible'],['#ff9800','Con turnos'],['#ef5350','Ausente'],['#546e7a','Sin agenda']].map(([c,lbl]) => (
          <Box key={lbl} sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
            <Box sx={{ width:7, height:7, borderRadius:'50%', bgcolor:c, opacity:0.75 }} />
            <Typography sx={{ fontSize:9, color:'#5a6a72' }}>{lbl}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Tarjeta de un turno ──────────────────────────────────────────────────────
function TurnoCard({ turno, onClick, rolActual, onAtender, onLlamar, onRecibir, onHistorial, llamadoOk }) {
  const est = estadoInfo(turno.estado);
  const hora = String(turno.hora_inicio).substring(0, 5);

  const esMedico      = rolActual === 'medico';
  const esStaffAdmin  = ['admin', 'recepcionista'].includes(rolActual);
  const yaAtendido    = turno.estado === 'atendido';
  const puedeRecibir  = esStaffAdmin && ['pendiente', 'confirmado'].includes(turno.estado);
  const puedeLlamar   = esMedico && ['confirmado', 'en_recepcion'].includes(turno.estado);
  const tieneHC       = esMedico && !!turno.paciente_id;

  const stopProp = (fn) => (e) => { e.stopPropagation(); fn(); };

  return (
    <Box sx={{
      mb: 1, borderRadius: 1.5,
      bgcolor: est.bg, border: `1px solid ${est.color}66`,
      // Turno especial: borde blanco del lado izquierdo -- tratamiento más evidente que en la
      // tabla de Turnos a propósito, porque acá el caso crítico es distinguirlo cuando hay varios
      // sobreturnos amontonados en la misma franja. El sobreturno ya usa rojo (⚡); especial usa
      // blanco para no competir con ese color.
      ...(turno.especial ? { borderLeft: '3px solid #fff' } : {}),
      overflow: 'hidden', transition: 'all 0.13s ease',
      '&:hover': { filter: 'brightness(1.12)', boxShadow: `0 4px 12px ${est.color}44` },
    }}>
      {/* Área principal — abre dialog */}
      <Box onClick={onClick} sx={{ cursor: 'pointer', p: '10px 12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: 0.3 }}>
            {hora}
            {!!turno.sobreturno && <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.9 }}>⚡</span>}
            {!!turno.especial && (
              <span title={turno.motivo_especial || 'Turno especial'}
                style={{ fontSize: 9, marginLeft: 4, padding: '1px 5px', borderRadius: 3, background: '#fff', color: '#1a1a1a', fontWeight: 700, letterSpacing: 0.3, verticalAlign: 'middle' }}>
                ✦ ESPECIAL
              </span>
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {turno.confirmacion_estado && (
              <span title="Confirmación de asistencia" style={{ fontSize: 11 }}>
                {turno.confirmacion_estado === 'confirmada' ? '🟢' : turno.confirmacion_estado === 'rechazada' ? '🔴' : '🟡'}
              </span>
            )}
            <Box sx={{
              fontSize: 9, px: 0.8, py: 0.2, borderRadius: 1,
              bgcolor: 'rgba(0,0,0,0.3)', color: 'rgba(255,255,255,0.85)',
              fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              {est.label}
            </Box>
          </Box>
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {turno.paciente_nombre || 'Sin nombre'}
        </Typography>
        {turno.motivo && (
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', mt: 0.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {turno.motivo}
          </Typography>
        )}
      </Box>

      {/* Barra de acciones rápidas */}
      {(puedeRecibir || puedeLlamar || tieneHC || (esMedico && !yaAtendido) || (esMedico && yaAtendido)) && (
        <Box sx={{ display: 'flex', borderTop: '1px solid rgba(255,255,255,0.1)', px: 0.5, py: 0.4, gap: 0.3 }}>
          {puedeRecibir && (
            <Button size="small" onClick={stopProp(onRecibir)}
              sx={{ fontSize: 10, color: '#a5d6a7', px: 0.8, py: 0.2, minWidth: 0, textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(102,187,106,0.15)' } }}>
              En recepción
            </Button>
          )}
          {puedeLlamar && (
            <Button size="small" onClick={stopProp(onLlamar)}
              sx={{ fontSize: 12, px: 0.8, py: 0.2, minWidth: 0,
                color: llamadoOk ? '#a5d6a7' : 'rgba(255,255,255,0.7)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}>
              {llamadoOk ? '✓' : '📢'}
            </Button>
          )}
          {esMedico && !yaAtendido && (
            <Button size="small" onClick={stopProp(onAtender)}
              sx={{ fontSize: 10, color: '#81c784', px: 0.8, py: 0.2, minWidth: 0, textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(102,187,106,0.15)' } }}>
              Atender
            </Button>
          )}
          {esMedico && yaAtendido && (
            <Button size="small" onClick={stopProp(onAtender)}
              sx={{ fontSize: 10, color: '#546e7a', px: 0.8, py: 0.2, minWidth: 0, textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
              Ver evolución
            </Button>
          )}
          {tieneHC && (
            <Button size="small" onClick={stopProp(onHistorial)}
              sx={{ fontSize: 10, color: '#ce93d8', px: 0.8, py: 0.2, minWidth: 0, textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(206,147,216,0.15)' } }}>
              Historial
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Columna de un médico ─────────────────────────────────────────────────────
function ColumnaMediaco({ medico, turnos, trabajaHoy, onTurnoClick, onSlotNuevo, onAbrirCal, colorIndex, fecha, rolActual, onAtender, onLlamar, onRecibir, onHistorial, llamadoOkId, esFavorito, onToggleFavorito, highlighted }) {
  const COLORES = ['#1565c0','#2e7d32','#ad1457','#e65100','#6a1b9a','#00695c','#4527a0','#37474f'];
  const colorDot = medico.color || COLORES[colorIndex % COLORES.length];
  const sorted = [...turnos].sort((a, b) => String(a.hora_inicio).localeCompare(String(b.hora_inicio)));

  const [mostrarLibres, setMostrarLibres] = useState(false);
  const [slotsLibres, setSlotsLibres]     = useState(null); // null | {slots:[],duracion}
  const [cargando, setCargando]           = useState(false);

  // Reset cuando cambia el día o hay nuevos turnos
  useEffect(() => {
    setMostrarLibres(false);
    setSlotsLibres(null);
  }, [fecha, turnos.length]);

  async function toggleLibres() {
    if (mostrarLibres) { setMostrarLibres(false); return; }
    if (slotsLibres) { setMostrarLibres(true); return; }
    setCargando(true);
    try {
      const r = await axios.get(`${API}/api/medicos/${medico.id}/slots?fecha=${fecha}`);
      setSlotsLibres(r.data);
      setMostrarLibres(true);
    } catch { setSlotsLibres({ slots: [], duracion: 30 }); setMostrarLibres(true); }
    finally { setCargando(false); }
  }

  const cantLibres = slotsLibres?.slots?.length ?? null;

  return (
    <Box data-medico-id={medico.id} sx={{ minWidth: 210, flex: 1, display: 'flex', flexDirection: 'column', borderRadius: '10px', transition: 'box-shadow 0.25s', boxShadow: highlighted ? '0 0 0 2px #1976d2, 0 0 16px rgba(25,118,210,0.25)' : 'none' }}>
      {/* Header del médico */}
      <Box sx={{
        px: 1.5, py: 1.2, bgcolor: 'var(--c-primario)',
        borderRadius: '10px 10px 0 0',
        borderBottom: `2px solid ${colorDot}`,
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorDot, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#e0e0e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {medico.nombre}
          </Typography>
          <Typography sx={{ fontSize: 10, color: 'var(--c-acento)', opacity: 0.8 }}>
            {medico.especialidad}
          </Typography>
        </Box>
        <Box sx={{ display:'flex', alignItems:'center', gap:0.5, flexShrink:0 }}>
          <Box sx={{ textAlign:'right' }}>
            <Typography sx={{ fontSize:13, fontWeight:800, color: turnos.length > 0 ? 'var(--c-acento)' : '#555' }}>
              {turnos.length}
            </Typography>
            <Typography sx={{ fontSize:9, color:'#555', lineHeight:1 }}>turnos</Typography>
          </Box>
          <Tooltip title="Ver disponibilidad" arrow>
            <IconButton size="small" onClick={() => onAbrirCal(medico)}
              sx={{ color:'var(--c-acento)', opacity:0.8, p:0.4, '&:hover':{ opacity:1, bgcolor:'rgba(255,255,255,0.08)' } }}>
              <Typography sx={{ fontSize:14, lineHeight:1 }}>📅</Typography>
            </IconButton>
          </Tooltip>
          <Tooltip title={esFavorito ? 'Quitar de favoritos' : 'Agregar a favoritos'} arrow>
            <IconButton size="small"
              onClick={e => { e.stopPropagation(); onToggleFavorito(medico.id); }}
              sx={{ p:0.3, transition:'color 0.15s', color: esFavorito ? '#ffb74d' : '#37474f',
                '&:hover':{ bgcolor:'rgba(255,255,255,0.06)', color: esFavorito ? '#ff9800' : '#607d8b' } }}>
              <Typography sx={{ fontSize:15, lineHeight:1 }}>{esFavorito ? '★' : '☆'}</Typography>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Cuerpo + botón */}
      <Box sx={{
        p: 1, minHeight: 120,
        bgcolor: 'rgba(0,0,0,0.18)',
        borderRadius: '0 0 10px 10px',
        border: '1px solid rgba(255,255,255,0.05)',
        borderTop: 'none',
      }}>
        {!trabajaHoy && turnos.length === 0 ? (
          <Typography sx={{ fontSize: 11, color: '#3d4f57', textAlign: 'center', mt: 3, fontStyle: 'italic' }}>
            Sin agenda hoy
          </Typography>
        ) : turnos.length === 0 ? (
          <Typography sx={{ fontSize: 11, color: '#3d4f57', textAlign: 'center', mt: 3 }}>
            Sin turnos
          </Typography>
        ) : (
          sorted.map(t => (
            <TurnoCard
              key={t.id} turno={t}
              onClick={() => onTurnoClick(t)}
              rolActual={rolActual}
              onAtender={() => onAtender(t)}
              onLlamar={() => onLlamar(t)}
              onRecibir={() => onRecibir(t)}
              onHistorial={() => onHistorial(t)}
              llamadoOk={llamadoOkId === t.id}
            />
          ))
        )}

        {/* Botón de horarios libres */}
        <Box
          onClick={toggleLibres}
          sx={{
            mt: 1, mx: -1, mb: -1,
            px: 1.5, py: 1, cursor: 'pointer',
            bgcolor: mostrarLibres ? '#2e7d32' : '#388e3c',
            borderRadius: '0 0 10px 10px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            transition: 'all 0.15s',
            '&:hover': { bgcolor: '#43a047' },
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            {cargando
              ? <CircularProgress size={11} sx={{ color: '#a5d6a7' }} />
              : <Typography sx={{ fontSize: 12 }}>🕐</Typography>
            }
            <Typography sx={{ fontSize: 11, color: '#a5d6a7', fontWeight: 600 }}>
              {cargando ? 'Cargando…'
                : cantLibres === null ? 'Ver horarios libres'
                : cantLibres === 0 ? 'Sin horarios libres'
                : `${cantLibres} horario${cantLibres !== 1 ? 's' : ''} libre${cantLibres !== 1 ? 's' : ''}`
              }
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 11, color: '#a5d6a7', fontWeight: 700 }}>
            {mostrarLibres ? '▲' : '▼'}
          </Typography>
        </Box>
      </Box>

      {/* Panel de slots libres */}
      {mostrarLibres && slotsLibres && (
        <Box sx={{
          p: 1.2, bgcolor: 'rgba(102,187,106,0.06)',
          border: '1px solid rgba(102,187,106,0.2)',
          borderTop: 'none',
          borderRadius: '0 0 10px 10px',
        }}>
          {slotsLibres.ausente ? (
            <Typography sx={{ fontSize: 11, color: '#ef9a9a', textAlign: 'center', py: 1 }}>
              ⚠️ Ausencia registrada hoy
            </Typography>
          ) : slotsLibres.slots.length === 0 ? (
            <Typography sx={{ fontSize: 11, color: '#5a6a72', textAlign: 'center', py: 1 }}>
              Sin horarios disponibles
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontSize: 9, color: '#5a6a72', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.8 }}>
                Libres · {slotsLibres.duracion} min c/u
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {slotsLibres.slots.map(hora => (
                  <Box key={hora} onClick={() => onSlotNuevo(medico.id, hora)} sx={{
                    px: 1.2, py: 0.4, borderRadius: '20px', fontSize: 11, fontWeight: 600,
                    bgcolor: 'rgba(102,187,106,0.15)', color: '#81c784',
                    border: '1px solid rgba(102,187,106,0.35)',
                    cursor: 'pointer', transition: 'all 0.12s',
                    '&:hover': { bgcolor: 'rgba(102,187,106,0.35)', color: '#fff', transform: 'scale(1.05)' },
                  }}>
                    {hora}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Helpers de disponibilidad ────────────────────────────────────────────────
function slotMins(t) { const [h, m] = t.split(':').map(Number); return h * 60 + m; }

function estimarTotalSlots(medicoId, fecha, disponibilidades) {
  const diaNom = DIAS_MAP_CAL[new Date(fecha + 'T12:00:00').getDay()];
  const hs = (disponibilidades[medicoId] || []).filter(h => h.dia === diaNom);
  return hs.reduce((sum, h) => sum + Math.floor((slotMins(h.hasta) - slotMins(h.desde)) / (h.duracion || 30)), 0);
}

function getRangoHorario(medicoId, fecha, disponibilidades) {
  const diaNom = DIAS_MAP_CAL[new Date(fecha + 'T12:00:00').getDay()];
  const hs = (disponibilidades[medicoId] || []).filter(h => h.dia === diaNom);
  if (!hs.length) return null;
  const desde = hs.reduce((mn, h) => h.desde < mn ? h.desde : mn, hs[0].desde);
  const hasta = hs.reduce((mx, h) => h.hasta > mx ? h.hasta : mx, hs[0].hasta);
  return `${desde.substring(0, 5)}–${hasta.substring(0, 5)}`;
}

// Un turno "cuenta" para la ocupación del día solo si coincide con un slot real
// de la grilla (mismo criterio que usa el panel de detalle para marcar "ocupado"):
// dentro del bloque de horario Y alineado a la duración del turno, no solo dentro
// del rango de horas. Turnos cargados fuera de grilla (agenda vieja, horario
// cambiado después) no deben inflar el conteo ni disparar "Sin lugar" en falso.
function turnoDentroDeHorario(horaInicio, medicoId, fecha, disponibilidades) {
  const diaNom = DIAS_MAP_CAL[new Date(fecha + 'T12:00:00').getDay()];
  const hs = (disponibilidades[medicoId] || []).filter(h => h.dia === diaNom);
  if (!hs.length) return true; // sin horario cargado: no filtramos, mantiene el comportamiento anterior
  const mins = slotMins(String(horaInicio).substring(0, 5));
  return hs.some(h => {
    const desde = slotMins(h.desde), hasta = slotMins(h.hasta), dur = h.duracion || 30;
    return mins >= desde && mins < hasta && (mins - desde) % dur === 0;
  });
}

// ── Tarjeta resumen en la grilla (sin acciones, solo estado) ─────────────────
function MedicoResumenCard({ medico, turnos, estimatedTotal, horario, isSelected, esFavorito, onToggleFavorito, onClick }) {
  const libres    = Math.max(0, estimatedTotal - turnos.length);
  const recepcion = turnos.filter(t => t.estado === 'en_recepcion').length;

  return (
    <Box onClick={onClick} sx={{
      border: isSelected ? '1.5px solid #1976d2' : '1px solid rgba(255,255,255,0.07)',
      borderRadius: '10px', p: '13px 13px 11px', cursor: 'pointer',
      bgcolor: isSelected ? 'rgba(25,118,210,0.12)' : '#111b23',
      transition: 'all 0.12s', position: 'relative',
      '&:hover': {
        borderColor: isSelected ? '#1976d2' : 'rgba(255,255,255,0.14)',
        bgcolor: isSelected ? 'rgba(25,118,210,0.12)' : '#17232e',
      },
      '&:hover .fav-btn': { opacity: 1 },
    }}>
      {onToggleFavorito && (
        <Box className="fav-btn" onClick={e => { e.stopPropagation(); onToggleFavorito(medico.id); }}
          sx={{
            position: 'absolute', top: 6, right: 6,
            opacity: esFavorito ? 1 : 0,
            fontSize: 11, lineHeight: 1, cursor: 'pointer',
            color: esFavorito ? '#ffb74d' : 'rgba(255,255,255,0.25)',
            transition: 'opacity 0.15s, color 0.15s',
            '&:hover': { color: '#ffb74d' },
          }}>
          ★
        </Box>
      )}
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#dde6ed', mb: 0.2,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 2 }}>
        {medico.nombre}
      </Typography>
      <Typography sx={{ fontSize: 10, color: 'rgba(144,202,249,0.55)', mb: 0.7 }}>
        {medico.especialidad}
      </Typography>
      {horario && (
        <Typography sx={{ fontSize: 10, color: '#546e7a', fontWeight: 500, mb: 0.8 }}>
          {horario}
        </Typography>
      )}
      {estimatedTotal > 0 ? (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#c8d8e4', mb: 0.15 }}>
            {turnos.length} / {estimatedTotal}
          </Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 600,
            color: libres === 0 ? '#ef9a9a' : libres <= 2 ? '#ffb74d' : '#81c784' }}>
            {libres > 0 ? `${libres} libres` : 'Sin lugar'}
          </Typography>
          {recepcion > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.6 }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#66bb6a' }} />
              <Typography sx={{ fontSize: 9, color: '#81c784' }}>{recepcion} en recepción</Typography>
            </Box>
          )}
        </>
      ) : (
        <Typography sx={{ fontSize: 10, color: '#37474f', fontStyle: 'italic' }}>Sin agenda</Typography>
      )}
    </Box>
  );
}

// ── Panel de agenda lateral ──────────────────────────────────────────────────
// Este componente no tiene lógica de negocio propia.
// Muestra la grilla horaria completa y usa TurnoCard con los mismos handlers
// que ya existen en el calendario — una sola implementación de acciones.
function PanelAgenda({ medico, turnos, slots, cargando, rolActual,
                       onTurnoClick, onSlotNuevo, onAtender, onLlamar, onRecibir, onHistorial,
                       llamadoOkId, onClose, onPrev, onNext, scrollMemory }) {
  const timelineRef = useRef(null);

  // Mezcla el grid horario con los turnos reales
  const timeline = useMemo(() => {
    if (!slots) return [];
    if (slots.ausente) return [{ tipo: 'ausente' }];
    return (slots.slots || []).map(s => {
      const hora    = typeof s === 'object' ? s.hora    : s;
      const ocupado = typeof s === 'object' ? s.ocupado : false;
      if (!ocupado) return { tipo: 'libre', hora };
      // Puede haber más de un turno en el mismo horario (sobreturno) -- hay que traerlos todos,
      // no solo el primero, o el/los sobreturno(s) quedan invisibles en la agenda.
      const turnosDelSlot = turnos.filter(t => String(t.hora_inicio).substring(0, 5) === hora);
      return turnosDelSlot.length ? { tipo: 'turno', hora, turnos: turnosDelSlot } : { tipo: 'libre', hora };
    });
  }, [slots, turnos]);

  // Restaura el scroll al volver a este médico
  useEffect(() => {
    const saved = scrollMemory.current[medico.id];
    if (timelineRef.current && saved !== undefined) {
      timelineRef.current.scrollTop = saved;
    }
  }, [medico.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalSlots = slots?.slots?.length || 0;
  const libres     = timeline.filter(s => s.tipo === 'libre').length;
  const pct        = totalSlots > 0 ? Math.round(((totalSlots - libres) / totalSlots) * 100) : 0;
  const barColor   = libres === 0 ? '#e53935' : libres <= 2 ? '#f57c00' : '#1976d2';

  return (
    <Box sx={{ width: '38%', flexShrink: 0, display: 'flex', flexDirection: 'column', borderLeft: '1px solid rgba(255,255,255,0.06)' }}>

      {/* ── Header del panel ── */}
      <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.06)', bgcolor: 'var(--c-primario)', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.6 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#e0e0e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {medico.nombre}
            </Typography>
            <Typography sx={{ fontSize: 10, color: 'var(--c-acento)', opacity: 0.8 }}>{medico.especialidad}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.2, ml: 1, flexShrink: 0 }}>
            <Tooltip title="Médico anterior (←)" arrow>
              <IconButton size="small" onClick={onPrev} sx={{ color: 'var(--c-acento)', p: 0.3, opacity: 0.7, '&:hover': { opacity: 1 } }}>
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Médico siguiente (→)" arrow>
              <IconButton size="small" onClick={onNext} sx={{ color: 'var(--c-acento)', p: 0.3, opacity: 0.7, '&:hover': { opacity: 1 } }}>
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cerrar panel (Esc)" arrow>
              <IconButton size="small" onClick={onClose} sx={{ color: '#546e7a', p: 0.3, '&:hover': { color: '#90a4ae' } }}>
                <Typography sx={{ fontSize: 13, lineHeight: 1 }}>✕</Typography>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {totalSlots > 0 && (
          <>
            <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden', mb: 0.4 }}>
              <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: 2, bgcolor: barColor, transition: 'width 0.3s' }} />
            </Box>
            <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
              {totalSlots - libres}/{totalSlots} ocupados ·{' '}
              <Box component="span" sx={{ fontWeight: 600, color: libres === 0 ? '#ef9a9a' : libres <= 2 ? '#ffb74d' : '#81c784' }}>
                {libres > 0 ? `${libres} libres` : 'completo'}
              </Box>
            </Typography>
          </>
        )}
      </Box>

      {/* ── Grilla horaria ── */}
      <Box
        ref={timelineRef}
        onScroll={() => { if (timelineRef.current) scrollMemory.current[medico.id] = timelineRef.current.scrollTop; }}
        sx={{ flex: 1, overflowY: 'auto', bgcolor: 'rgba(0,0,0,0.15)' }}
      >
        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={22} sx={{ color: 'var(--c-acento)' }} />
          </Box>
        ) : timeline.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#3d4f57', textAlign: 'center', mt: 4, fontStyle: 'italic' }}>
            Sin agenda hoy
          </Typography>
        ) : (
          timeline.map((slot, i) => (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'stretch',
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              bgcolor: slot.tipo === 'libre' ? 'rgba(102,187,106,0.04)' : 'transparent',
              '&:hover': { bgcolor: slot.tipo === 'libre' ? 'rgba(102,187,106,0.08)' : 'rgba(255,255,255,0.02)' },
            }}>
              {/* Columna de hora */}
              <Box sx={{
                width: 46, px: 1, py: slot.tipo === 'turno' ? 1 : 0.8,
                flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                borderRight: `2px solid ${slot.tipo === 'libre' ? 'rgba(102,187,106,0.2)' : 'rgba(255,255,255,0.05)'}`,
                fontSize: 10, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
                color: slot.tipo === 'libre' ? 'rgba(102,187,106,0.7)' : 'rgba(255,255,255,0.25)',
              }}>
                {slot.hora}
              </Box>

              {/* Contenido del slot */}
              <Box sx={{ flex: 1, p: slot.tipo === 'turno' ? '8px 8px 8px 10px' : '8px 10px', minWidth: 0 }}>
                {slot.tipo === 'ausente' && (
                  <Typography sx={{ fontSize: 11, color: '#ef9a9a', py: 2, textAlign: 'center' }}>
                    ⚠️ Ausencia registrada hoy
                  </Typography>
                )}

                {slot.tipo === 'libre' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 32 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#43a047', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#81c784', flex: 1 }}>Disponible</Typography>
                    <Box
                      onClick={() => onSlotNuevo(medico.id, slot.hora)}
                      sx={{
                        px: 1.2, py: 0.3, borderRadius: '20px', fontSize: 10, fontWeight: 600,
                        bgcolor: 'rgba(102,187,106,0.12)', color: '#81c784',
                        border: '1px solid rgba(102,187,106,0.25)',
                        cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
                        '&:hover': { bgcolor: 'rgba(102,187,106,0.3)', color: '#fff' },
                      }}>
                      Reservar
                    </Box>
                  </Box>
                )}

                {/* Una TurnoCard por cada turno del slot -- si hay sobreturno(s) coincidiendo
                    en el mismo horario, se ven todos, no solo el primero. */}
                {slot.tipo === 'turno' && slot.turnos.map(turno => (
                  <TurnoCard
                    key={turno.id}
                    turno={turno}
                    onClick={() => onTurnoClick(turno)}
                    rolActual={rolActual}
                    onAtender={() => onAtender(turno)}
                    onLlamar={() => onLlamar(turno)}
                    onRecibir={() => onRecibir(turno)}
                    onHistorial={() => onHistorial(turno)}
                    llamadoOk={llamadoOkId === turno.id}
                  />
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* ── Pie: Nuevo turno general (no depende de horario libre) ── */}
      <Box sx={{ p: 1.2, borderTop: '1px solid rgba(255,255,255,0.06)', bgcolor: 'var(--c-primario)', flexShrink: 0 }}>
        <Button fullWidth size="small" startIcon={<AddIcon />}
          onClick={() => onSlotNuevo(medico.id, '09:00')}
          sx={{
            color: 'var(--c-acento)', border: '1px solid rgba(144,202,249,0.2)',
            textTransform: 'none', fontSize: 11,
            '&:hover': { border: '1px solid rgba(144,202,249,0.4)', bgcolor: 'rgba(144,202,249,0.04)' },
          }}>
          Nuevo turno
        </Button>
      </Box>
    </Box>
  );
}

// ── Stat chip ────────────────────────────────────────────────────────────────
function StatChip({ label, value, color }) {
  return (
    <Box sx={{
      px: 2, py: 1, borderRadius: 2,
      bgcolor: `${color}18`, border: `1px solid ${color}44`,
      display: 'flex', alignItems: 'baseline', gap: 0.8,
    }}>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: `${color}bb`, fontWeight: 500 }}>{label}</Typography>
    </Box>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function Calendario() {
  const navigate = useNavigate();
  const rolActual = sessionStorage.getItem('rol') || '';

  const [fechaDia, setFechaDia]             = useState(hoy);
  const [turnos, setTurnos]                 = useState([]);
  const [medicos, setMedicos]               = useState([]);
  const [obrasSociales, setObrasSociales]   = useState([]);
  const [disponibilidades, setDisponibilidades] = useState({});
  const [loading, setLoading]               = useState(true);

  const [dialogOpen, setDialogOpen]         = useState(false);
  const [turnoId, setTurnoId]               = useState(null);
  const [turnoObj, setTurnoObj]             = useState(null);
  const [dialogInitialValues, setDialogInitialValues] = useState(null);
  const [dialogModoRecibir, setDialogModoRecibir] = useState(false);

  // Turnero
  const [llamando, setLlamando]             = useState(false);
  const [llamadoOkId, setLlamadoOkId]       = useState(null);
  const [consultorioModal, setConsultorioModal]     = useState(false);
  const [consultorioSeleccion, setConsultorioSeleccion] = useState('Consultorio 1');
  const [turnoALlamar, setTurnoALlamar]     = useState(null);

  // ── Vista Calendario por médico ──
  const [calPickerOpen, setCalPickerOpen]       = useState(false);
  const [calPickerMedico, setCalPickerMedico]   = useState(null);
  const [calPickerMes, setCalPickerMes]         = useState(() => hoy().substring(0,7));
  const [calPickerOcupacion, setCalPickerOcupacion] = useState({});
  const [calPickerExtraordinaria, setCalPickerExtraordinaria] = useState({});
  const [calPickerFecha, setCalPickerFecha]     = useState('');
  const [calPickerSlots, setCalPickerSlots]     = useState(null);
  const [calPickerCargando, setCalPickerCargando] = useState(false);

  // ── Favoritos + búsqueda ──
  const [favoritos, setFavoritos] = useState(() => {
    const uid = sessionStorage.getItem('id') || 'anon';
    try { return JSON.parse(localStorage.getItem(`favoritos_${uid}`)) || []; }
    catch { return []; }
  });
  const [highlightId, setHighlightId]   = useState(null);
  const [busqueda, setBusqueda]         = useState('');
  const [sinAgendaMsg, setSinAgendaMsg] = useState('');
  const busRef = useRef(null);

  // ── Panel de agenda ──
  const [selectedMedicoId, setSelectedMedicoId] = useState(null);
  const [panelSlots, setPanelSlots]             = useState(null);
  const [panelCargando, setPanelCargando]       = useState(false);
  const panelScrollMemory                        = useRef({});

  // ── Grilla paginada ──
  const [paginaGrid, setPaginaGrid]                   = useState(0);
  const [sinAgendaColapsado, setSinAgendaColapsado]   = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const favoritosHoy = useMemo(() => {
    const diaNom = DIAS_MAP_CAL[new Date(fechaDia + 'T12:00:00').getDay()];
    return medicos.filter(m =>
      favoritos.includes(m.id) &&
      (disponibilidades[m.id] || []).some(h => h.dia === diaNom)
    );
  }, [medicos, favoritos, fechaDia, disponibilidades]); // eslint-disable-line

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const medicosOrdenados = useMemo(() => {
    const diaNom = DIAS_MAP_CAL[new Date(fechaDia + 'T12:00:00').getDay()];
    const esFavActivo = m => favoritos.includes(m.id) && (disponibilidades[m.id] || []).some(h => h.dia === diaNom);
    return [...medicos.filter(m => esFavActivo(m)), ...medicos.filter(m => !esFavActivo(m))];
  }, [medicos, favoritos, fechaDia, disponibilidades]); // eslint-disable-line

  const cargarTurnos = useCallback(async (fecha) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/turnos?fecha=${fecha}`);
      setTurnos(r.data.data || []);
    } catch { setTurnos([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    async function init() {
      try {
        const [medicosRes, dispRes] = await Promise.all([
          axios.get(`${API}/api/medicos`),
          axios.get(`${API}/api/medicos/disponibilidades`),
          axios.get(`${API}/api/obras-sociales`).then(r => setObrasSociales(r.data)).catch(() => {}),
        ]);
        const todosMedicos = medicosRes.data || [];
        const medicoIdSesion = sessionStorage.getItem('medico_id');
        setMedicos(rolActual === 'medico' && medicoIdSesion
          ? todosMedicos.filter(m => String(m.id) === String(medicoIdSesion))
          : todosMedicos);
        setDisponibilidades(dispRes.data || {});
      } catch {}
    }
    init();
  }, []);

  useEffect(() => { cargarTurnos(fechaDia); }, [fechaDia, cargarTurnos]);

  // ── Helpers ──
  const DIAS_MAP = { 0:'Domingo', 1:'Lunes', 2:'Martes', 3:'Miercoles', 4:'Jueves', 5:'Viernes', 6:'Sabado' };

  function medicoTrabajahoy(medicoId) {
    const diaNom = DIAS_MAP[new Date(fechaDia + 'T12:00:00').getDay()];
    return (disponibilidades[medicoId] || []).some(h => h.dia === diaNom);
  }

  // ── Stats ──
  const totalHoy   = turnos.length;
  const pendientes = turnos.filter(t => t.estado === 'pendiente').length;
  const enRecep    = turnos.filter(t => t.estado === 'en_recepcion').length;
  const atendidos  = turnos.filter(t => t.estado === 'atendido').length;
  const cancelados = turnos.filter(t => t.estado === 'cancelado').length;

  // ── Dialog handlers ──
  function abrirNuevo(medicoId, hora) {
    setTurnoId(null);
    setTurnoObj(null);
    setDialogInitialValues({ medico_id: medicoId || '', fecha: fechaDia, hora_inicio: hora || '09:00' });
    setDialogOpen(true);
  }

  function abrirEditar(t) {
    setTurnoId(t.id);
    setTurnoObj(t);
    setDialogInitialValues(null);
    setDialogOpen(true);
  }

  function cerrarDialog() {
    setDialogModoRecibir(false);
    setDialogOpen(false);
    setTurnoId(null);
    setTurnoObj(null);
    setDialogInitialValues(null);
  }

  function abrirRecibir(t) {
    setTurnoId(t.id);
    setTurnoObj(t);
    setDialogInitialValues(null);
    setDialogModoRecibir(true);
    setDialogOpen(true);
  }

  async function llamarPaciente(turno) {
    const consultorioGuardado = sessionStorage.getItem('consultorio');
    if (!consultorioGuardado) {
      setTurnoALlamar(turno);
      setConsultorioModal(true);
      return;
    }
    await ejecutarLlamado(turno, consultorioGuardado);
  }

  async function ejecutarLlamado(turno, consultorio) {
    setLlamando(true);
    try {
      await axios.post(`${API}/api/turnero/llamar`, {
        turno_id: turno.id,
        paciente_nombre: turno.paciente_nombre,
        consultorio,
      });
      setLlamadoOkId(turno.id);
      setTimeout(() => setLlamadoOkId(null), 3000);
    } catch {}
    setLlamando(false);
  }

  async function confirmarConsultorioYLlamar() {
    sessionStorage.setItem('consultorio', consultorioSeleccion);
    axios.patch(`${API}/api/medicos/consultorio`, { consultorio: consultorioSeleccion }).catch(() => {});
    setConsultorioModal(false);
    if (turnoALlamar) {
      await ejecutarLlamado(turnoALlamar, consultorioSeleccion);
      setTurnoALlamar(null);
    }
  }

  // ── Handlers de Vista Calendario ──
  async function abrirCalPicker(medico) {
    setCalPickerMedico(medico);
    setCalPickerFecha('');
    setCalPickerSlots(null);
    const mes = hoy().substring(0,7);
    setCalPickerMes(mes);
    setCalPickerOpen(true);
    cargarOcupacionCalPicker(medico.id, mes);
  }

  async function cargarOcupacionCalPicker(medicoId, mes) {
    try {
      const r = await axios.get(`${API}/api/medicos/${medicoId}/ocupacion-mes?mes=${mes}`);
      setCalPickerOcupacion(r.data?.ocupacion || {});
      setCalPickerExtraordinaria(r.data?.extraordinaria || {});
    } catch { setCalPickerOcupacion({}); setCalPickerExtraordinaria({}); }
  }

  async function handleCalPickerMesChange(nuevoMes) {
    setCalPickerMes(nuevoMes);
    setCalPickerFecha('');
    setCalPickerSlots(null);
    if (calPickerMedico) cargarOcupacionCalPicker(calPickerMedico.id, nuevoMes);
  }

  async function handleCalPickerFechaClick(fecha) {
    setCalPickerFecha(fecha);
    setCalPickerSlots(null);
    if (!calPickerMedico) return;
    setCalPickerCargando(true);
    try {
      const r = await axios.get(`${API}/api/medicos/${calPickerMedico.id}/slots?fecha=${fecha}&todos=1`);
      setCalPickerSlots(r.data);
    } catch { setCalPickerSlots({ slots: [], duracion: 30 }); }
    finally { setCalPickerCargando(false); }
  }

  function handleCalPickerSlotClick(hora, ocupado) {
    setCalPickerOpen(false);
    setTurnoId(null);
    setTurnoObj(null);
    setDialogInitialValues({ medico_id: calPickerMedico?.id || '', fecha: calPickerFecha, hora_inicio: hora, sobreturno: ocupado ? 1 : 0 });
    setDialogOpen(true);
  }


  // ── Favoritos helpers ────────────────────────────────────────────────────────
  function toggleFavorito(medicoId) {
    const uid = sessionStorage.getItem('id') || 'anon';
    setFavoritos(prev => {
      const next = prev.includes(medicoId)
        ? prev.filter(id => id !== medicoId)
        : [...prev, medicoId];
      localStorage.setItem(`favoritos_${uid}`, JSON.stringify(next));
      return next;
    });
  }

  function irAMedico(medicoId) {
    const el = document.querySelector(`[data-medico-id="${medicoId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      setHighlightId(medicoId);
      setTimeout(() => setHighlightId(null), 2000);
    }
  }

  function seleccionarDeBusqueda(m) {
    setBusqueda('');
    if (medicoTrabajahoy(m.id)) {
      irAMedico(m.id);
    } else {
      setSinAgendaMsg(`${m.nombre} — No tiene agenda hoy`);
      setTimeout(() => setSinAgendaMsg(''), 3000);
    }
  }

  // ── Panel de agenda: carga slots y abre el panel ────────────────────────────
  async function abrirPanel(medicoId) {
    setSelectedMedicoId(medicoId);
    setPanelSlots(null);
    setPanelCargando(true);
    try {
      const r = await axios.get(`${API}/api/medicos/${medicoId}/slots?fecha=${fechaDia}&todos=1`);
      setPanelSlots(r.data);
    } catch { setPanelSlots({ slots: [], duracion: 30, ausente: false }); }
    finally { setPanelCargando(false); }
  }

  function cerrarPanel() {
    setSelectedMedicoId(null);
    setPanelSlots(null);
  }

  function navPanel(dir) {
    const conAgenda = medicosOrdenados.filter(m => medicoTrabajahoy(m.id));
    if (!conAgenda.length) return;
    const idx  = conAgenda.findIndex(m => m.id === selectedMedicoId);
    const next = conAgenda[(idx + dir + conAgenda.length) % conAgenda.length];
    abrirPanel(next.id);
  }

  // Recarga el panel al cambiar de fecha y resetea la página de la grilla
  useEffect(() => {
    if (selectedMedicoId) abrirPanel(selectedMedicoId);
    setPaginaGrid(0);
  }, [fechaDia]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navegación por teclado cuando el panel está abierto
  useEffect(() => {
    if (!selectedMedicoId) return;
    function onKey(e) {
      if (dialogOpen || calPickerOpen || consultorioModal) return;
      if (e.key === 'ArrowLeft')       { e.preventDefault(); navPanel(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); navPanel(1); }
      else if (e.key === 'Escape')     cerrarPanel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedMedicoId, dialogOpen, calPickerOpen, consultorioModal, medicosOrdenados, fechaDia]); // eslint-disable-line react-hooks/exhaustive-deps

  const resultadosBusqueda = useMemo(() => {
    if (!busqueda.trim()) return [];
    const q = busqueda.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    return medicos.filter(m =>
      m.nombre.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').includes(q)
    ).slice(0, 8);
  }, [busqueda, medicos]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {/* Navegación de fecha */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={() => setFechaDia(f => sumarDias(f, -1))} sx={{ color: 'var(--c-acento)' }}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--c-acento)', textTransform: 'capitalize', mx: 1, minWidth: 280 }}>
            {formatFechaLarga(fechaDia)}
          </Typography>
          <IconButton size="small" onClick={() => setFechaDia(f => sumarDias(f, 1))} sx={{ color: 'var(--c-acento)' }}>
            <ChevronRightIcon />
          </IconButton>
          {fechaDia !== hoy() && (
            <Button size="small" startIcon={<TodayIcon sx={{ fontSize: 16 }} />}
              onClick={() => setFechaDia(hoy())}
              sx={{ color: '#ffb74d', textTransform: 'none', fontSize: 12 }}>
              Hoy
            </Button>
          )}
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirNuevo('')}
          sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
          Nuevo Turno
        </Button>
      </Box>

      {/* ── Favoritos + Búsqueda ── */}
      {rolActual !== 'medico' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap', minHeight: 34 }}>
          {/* Favoritos activos hoy */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, flexWrap: 'wrap' }}>
            {favoritosHoy.length > 0 && (
              <Typography sx={{ fontSize: 11, color: '#ffb74d', fontWeight: 700, opacity: 0.8, flexShrink: 0 }}>
                ⭐ {favoritosHoy.length}
              </Typography>
            )}
            {favoritosHoy.map(m => {
              const tieneRecepcion = turnos.some(t => t.medico_id === m.id && t.estado === 'en_recepcion');
              const nombre = m.nombre.split(' ').slice(0, 2).join(' ');
              return (
                <Box key={m.id} onClick={() => irAMedico(m.id)} sx={{
                  px: '10px', py: '3px', borderRadius: '20px', cursor: 'pointer',
                  bgcolor: 'rgba(255,183,77,0.1)', border: '1px solid rgba(255,183,77,0.25)',
                  display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.12s',
                  '&:hover': { bgcolor: 'rgba(255,183,77,0.2)', borderColor: 'rgba(255,183,77,0.5)' },
                }}>
                  <Typography sx={{ fontSize: 12, color: '#ffb74d', fontWeight: 600 }}>{nombre}</Typography>
                  {tieneRecepcion && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#66bb6a', flexShrink: 0 }} />}
                </Box>
              );
            })}
          </Box>

          {/* Buscador */}
          <Box sx={{ position: 'relative', flexShrink: 0 }} ref={busRef}>
            <Box component="input"
              placeholder="🔍 Buscar médico..."
              value={busqueda}
              onChange={e => { setBusqueda(e.target.value); setSinAgendaMsg(''); }}
              onBlur={() => setTimeout(() => setBusqueda(''), 200)}
              sx={{
                height: 32, px: '12px', borderRadius: '8px', fontSize: 12,
                border: '1px solid rgba(144,202,249,0.2)',
                bgcolor: 'var(--c-fondo)', color: 'var(--c-texto)',
                outline: 'none', width: 200, fontFamily: 'inherit',
                '&:focus': { borderColor: 'rgba(144,202,249,0.45)' },
              }}
            />
            {resultadosBusqueda.length > 0 && (
              <Box sx={{
                position: 'absolute', top: '100%', right: 0, mt: '4px', zIndex: 300,
                minWidth: 240, bgcolor: 'var(--c-superficie)',
                border: '1px solid rgba(144,202,249,0.15)',
                borderRadius: '8px', overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
              }}>
                {resultadosBusqueda.map(m => {
                  const tieneAgenda = medicoTrabajahoy(m.id);
                  return (
                    <Box key={m.id} onMouseDown={() => seleccionarDeBusqueda(m)} sx={{
                      px: '12px', py: '8px', cursor: 'pointer',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      borderBottom: '1px solid rgba(144,202,249,0.06)',
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { bgcolor: 'rgba(144,202,249,0.06)' },
                    }}>
                      <Typography sx={{ fontSize: 13, color: tieneAgenda ? 'var(--c-texto)' : '#546e7a' }}>
                        {m.nombre}
                      </Typography>
                      {!tieneAgenda && (
                        <Typography sx={{ fontSize: 10, color: '#455a64', fontStyle: 'italic', ml: 1 }}>
                          Sin agenda hoy
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
            {sinAgendaMsg && (
              <Box sx={{
                position: 'absolute', top: '100%', right: 0, mt: '4px', zIndex: 300, minWidth: 240,
                bgcolor: 'rgba(69,90,100,0.15)', border: '1px solid rgba(69,90,100,0.35)',
                borderRadius: '8px', px: '12px', py: '8px',
              }}>
                <Typography sx={{ fontSize: 12, color: '#78909c', fontStyle: 'italic' }}>
                  {sinAgendaMsg}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ── Stats ── */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
        <StatChip label="total" value={totalHoy} color="#90caf9" />
        <StatChip label="pendientes" value={pendientes} color="#1565c0" />
        <StatChip label="en recepción" value={enRecep} color="#6a1b9a" />
        <StatChip label="atendidos" value={atendidos} color="#37474f" />
        {cancelados > 0 && <StatChip label="cancelados" value={cancelados} color="#b71c1c" />}
      </Box>

      {/* ── Grilla de médicos + Panel de agenda ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress sx={{ color: 'var(--c-acento)' }} />
        </Box>
      ) : medicos.length === 0 ? (
        <Typography sx={{ color: '#555', textAlign: 'center', mt: 6 }}>No hay médicos registrados.</Typography>
      ) : (
        <Box sx={{
          display: 'flex', height: 'calc(100vh - 260px)', minHeight: 480,
          border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', overflow: 'hidden',
        }}>
          {/* Grilla organizada: chips favoritos → agenda paginada → sin agenda → resumen */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'var(--c-fondo)', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {(() => {
              const PAGE_SIZE    = 10;
              const conAgenda    = medicosOrdenados.filter(m => medicoTrabajahoy(m.id));
              const sinAgenda    = medicosOrdenados.filter(m => !medicoTrabajahoy(m.id));
              const totalPaginas = Math.max(1, Math.ceil(conAgenda.length / PAGE_SIZE));
              const paginaSegura = Math.min(paginaGrid, totalPaginas - 1);
              const paginaMeds   = conAgenda.slice(paginaSegura * PAGE_SIZE, (paginaSegura + 1) * PAGE_SIZE);
              const vacias       = Math.max(0, PAGE_SIZE - paginaMeds.length);

              const cardProps = (m) => ({
                medico: m,
                turnos: turnos.filter(t => t.medico_id === m.id && turnoDentroDeHorario(t.hora_inicio, m.id, fechaDia, disponibilidades)),
                estimatedTotal: estimarTotalSlots(m.id, fechaDia, disponibilidades),
                horario: getRangoHorario(m.id, fechaDia, disponibilidades),
                isSelected: selectedMedicoId === m.id,
                esFavorito: favoritos.includes(m.id),
                onToggleFavorito: rolActual !== 'medico' ? toggleFavorito : null,
                onClick: () => abrirPanel(m.id),
              });

              const chipFavClick = (m) => {
                const idx = conAgenda.findIndex(gm => gm.id === m.id);
                if (idx >= 0) setPaginaGrid(Math.floor(idx / PAGE_SIZE));
                abrirPanel(m.id);
              };

              const totalSlots  = conAgenda.reduce((s, m) => s + estimarTotalSlots(m.id, fechaDia, disponibilidades), 0);
              const turnosEnHorario = turnos.filter(t => {
                const m = conAgenda.find(gm => gm.id === t.medico_id);
                return m && turnoDentroDeHorario(t.hora_inicio, m.id, fechaDia, disponibilidades);
              });
              const totalLibres = Math.max(0, totalSlots - turnosEnHorario.length);

              return (
                <>
                  {/* ── Chips de favoritos ── */}
                  {favoritosHoy.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', pb: 1.5,
                      borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {favoritosHoy.map(m => (
                        <Box key={m.id} onClick={() => chipFavClick(m)} sx={{
                          display: 'flex', alignItems: 'center', gap: 0.7,
                          px: 1.5, py: 0.7, borderRadius: '20px', cursor: 'pointer',
                          border: selectedMedicoId === m.id
                            ? '1.5px solid #1976d2'
                            : '1px solid rgba(255,183,77,0.25)',
                          bgcolor: selectedMedicoId === m.id
                            ? 'rgba(25,118,210,0.15)'
                            : 'rgba(255,183,77,0.06)',
                          transition: 'all 0.12s',
                          '&:hover': { borderColor: '#ffb74d', bgcolor: 'rgba(255,183,77,0.1)' },
                        }}>
                          <Typography sx={{ fontSize: 9, color: '#ffb74d' }}>⭐</Typography>
                          <Typography sx={{ fontSize: 11, fontWeight: 600,
                            color: selectedMedicoId === m.id ? '#90caf9' : '#c8d8e4' }}>
                            {m.nombre.split(' ').slice(0, 2).join(' ')}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* ── Agenda de hoy paginada ── */}
                  <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.25)', letterSpacing: 1 }}>
                    AGENDA DE HOY ({conAgenda.length})
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '8px' }}>
                    {paginaMeds.map(m => <MedicoResumenCard key={m.id} {...cardProps(m)} />)}
                    {Array.from({ length: vacias }).map((_, i) => (
                      <Box key={`v${i}`} sx={{ borderRadius: '10px',
                        border: '1px dashed rgba(255,255,255,0.04)', minHeight: 90 }} />
                    ))}
                  </Box>

                  {/* ── Paginación ── */}
                  {totalPaginas > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <IconButton size="small" disabled={paginaSegura === 0}
                        onClick={() => setPaginaGrid(p => Math.max(0, p - 1))}
                        sx={{ color: 'var(--c-acento)', p: 0.4 }}>
                        <ChevronLeftIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      {Array.from({ length: totalPaginas }).map((_, i) => (
                        <Box key={i} onClick={() => setPaginaGrid(i)} sx={{
                          width: 24, height: 24, borderRadius: 1, cursor: 'pointer',
                          bgcolor: paginaSegura === i ? 'var(--c-acento)' : 'transparent',
                          color: paginaSegura === i ? '#000' : 'rgba(255,255,255,0.35)',
                          fontSize: 11, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          '&:hover': { bgcolor: paginaSegura === i ? 'var(--c-acento)' : 'rgba(255,255,255,0.06)' },
                        }}>
                          {i + 1}
                        </Box>
                      ))}
                      <IconButton size="small" disabled={paginaSegura === totalPaginas - 1}
                        onClick={() => setPaginaGrid(p => Math.min(totalPaginas - 1, p + 1))}
                        sx={{ color: 'var(--c-acento)', p: 0.4 }}>
                        <ChevronRightIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}

                  {/* ── Sin agenda ── */}
                  {sinAgenda.length > 0 && (
                    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.05)', pt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: 11, color: '#37474f' }}>
                          {sinAgenda.length} médicos sin agenda hoy
                        </Typography>
                        <Button size="small" onClick={() => setSinAgendaColapsado(v => !v)}
                          sx={{ fontSize: 10, color: '#546e7a', textTransform: 'none', py: 0.3,
                            '&:hover': { color: '#78909c', bgcolor: 'transparent' } }}>
                          {sinAgendaColapsado ? 'Ver listado' : 'Ocultar'}
                        </Button>
                      </Box>
                      {!sinAgendaColapsado && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '8px', mt: 1 }}>
                          {sinAgenda.map(m => <MedicoResumenCard key={m.id} {...cardProps(m)} />)}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* ── Resumen del día ── */}
                  <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid rgba(255,255,255,0.04)',
                    display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#546e7a', lineHeight: 1 }}>{conAgenda.length}</Typography>
                      <Typography sx={{ fontSize: 9, color: '#37474f', mt: 0.3 }}>médicos hoy</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#546e7a', lineHeight: 1 }}>{turnos.filter(t => conAgenda.some(m => m.id === t.medico_id)).length}</Typography>
                      <Typography sx={{ fontSize: 9, color: '#37474f', mt: 0.3 }}>turnos</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#2e7d32', lineHeight: 1 }}>{totalLibres}</Typography>
                      <Typography sx={{ fontSize: 9, color: '#37474f', mt: 0.3 }}>libres</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#546e7a', lineHeight: 1 }}>{totalSlots}</Typography>
                      <Typography sx={{ fontSize: 9, color: '#37474f', mt: 0.3 }}>slots totales</Typography>
                    </Box>
                  </Box>
                </>
              );
            })()}
          </Box>

          {/* Panel de agenda lateral */}
          {selectedMedicoId && (() => {
            const med = medicos.find(m => m.id === selectedMedicoId);
            if (!med) return null;
            return (
              <PanelAgenda
                medico={med}
                turnos={turnos.filter(t => t.medico_id === selectedMedicoId)}
                slots={panelSlots}
                cargando={panelCargando}
                rolActual={rolActual}
                onTurnoClick={abrirEditar}
                onSlotNuevo={(medicoId, hora) => abrirNuevo(medicoId, hora || '09:00')}
                onAtender={(t) => navigate(`/consulta/${t.id}`)}
                onLlamar={llamarPaciente}
                onRecibir={abrirRecibir}
                onHistorial={(t) => navigate(`/historial/${t.paciente_id}`)}
                llamadoOkId={llamadoOkId}
                onClose={cerrarPanel}
                onPrev={() => navPanel(-1)}
                onNext={() => navPanel(1)}
                scrollMemory={panelScrollMemory}
              />
            );
          })()}
        </Box>
      )}

      {/* ── Dialog Vista Calendario por médico ── */}
      <Dialog open={calPickerOpen} onClose={() => setCalPickerOpen(false)} maxWidth="sm" fullWidth
        PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
            <Typography sx={{ fontSize:16 }}>📅</Typography>
            <Box>
              <Typography sx={{ fontWeight:700, fontSize:15, color:'#e0e0e0' }}>
                {calPickerMedico?.nombre}
              </Typography>
              <Typography sx={{ fontSize:11, color:'var(--c-acento)', opacity:0.8 }}>
                {calPickerMedico?.especialidad}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display:'flex', gap:3, flexWrap:'wrap' }}>
            {/* Calendariito mensual */}
            <Box sx={{ flex:'0 0 260px' }}>
              <MiniCalendarioPicker
                mes={calPickerMes}
                onMesChange={handleCalPickerMesChange}
                ocupacion={calPickerOcupacion}
                extraordinaria={calPickerExtraordinaria}
                horarios={disponibilidades[calPickerMedico?.id] || []}
                fechaSeleccionada={calPickerFecha}
                onFechaClick={handleCalPickerFechaClick}
              />
            </Box>
            {/* Panel de slots */}
            <Box sx={{ flex:1, minWidth:180, pt:0.5 }}>
              {!calPickerFecha ? (
                <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', opacity:0.4, pt:4 }}>
                  <Typography sx={{ fontSize:28 }}>👆</Typography>
                  <Typography sx={{ fontSize:12, color:'#90a4ae', textAlign:'center', mt:1 }}>
                    Seleccioná una fecha para ver los horarios
                  </Typography>
                </Box>
              ) : calPickerCargando ? (
                <Box sx={{ display:'flex', justifyContent:'center', pt:4 }}>
                  <CircularProgress size={24} sx={{ color:'var(--c-acento)' }} />
                </Box>
              ) : (
                <Box>
                  <Typography sx={{ fontSize:12, color:'var(--c-acento)', fontWeight:600, mb:1.5, textTransform:'capitalize' }}>
                    {new Date(calPickerFecha+'T12:00:00').toLocaleDateString('es-AR',{ weekday:'long', day:'numeric', month:'long' })}
                  </Typography>
                  {!calPickerSlots ? null :
                    calPickerSlots.ausente ? (
                      <Typography sx={{ fontSize:12, color:'#ef9a9a' }}>⚠️ Ausencia registrada</Typography>
                    ) : calPickerSlots.slots?.length === 0 ? (
                      <Typography sx={{ fontSize:12, color:'#5a6a72' }}>Sin horarios disponibles</Typography>
                    ) : (
                      <>
                        <Typography sx={{ fontSize:10, color:'#5a6a72', fontWeight:700, textTransform:'uppercase', letterSpacing:1, mb:1 }}>
                          Horarios · {calPickerSlots.duracion} min
                        </Typography>
                        <Box sx={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                          {calPickerSlots.slots.map(s => {
                            const hora    = typeof s === 'object' ? s.hora    : s;
                            const ocupado = typeof s === 'object' ? s.ocupado : false;
                            return (
                              <Tooltip key={hora} title={ocupado ? '⚡ Ocupado — sobreturno' : 'Disponible'} arrow>
                                <Box onClick={() => handleCalPickerSlotClick(hora, ocupado)} sx={{
                                  px:1.5, py:0.6, borderRadius:'20px', fontSize:12, fontWeight:500,
                                  cursor:'pointer', border:'1px solid', minWidth:52, textAlign:'center',
                                  bgcolor: ocupado ? 'rgba(211,47,47,0.12)' : 'rgba(102,187,106,0.12)',
                                  color:   ocupado ? '#ef9a9a' : '#81c784',
                                  borderColor: ocupado ? 'rgba(211,47,47,0.3)' : 'rgba(102,187,106,0.3)',
                                  transition:'all 0.12s',
                                  '&:hover': { filter:'brightness(1.3)', transform:'scale(1.05)' },
                                }}>
                                  {hora}
                                  {ocupado && <span style={{ fontSize:7, marginLeft:3, opacity:0.6 }}>●</span>}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </>
                    )
                  }
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalPickerOpen(false)} sx={{ color:'var(--c-acento)' }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <TurnoDialog
        open={dialogOpen}
        onClose={cerrarDialog}
        onSuccess={() => cargarTurnos(fechaDia)}
        turnoId={turnoId}
        turno={turnoObj}
        disponibilidades={disponibilidades}
        medicos={medicos}
        obrasSociales={obrasSociales}
        initialValues={dialogInitialValues}
        modoRecibir={dialogModoRecibir}
        origen="Calendario"
      />

      {/* ── Modal consultorio (médico) ── */}
      <Dialog open={consultorioModal} onClose={() => setConsultorioModal(false)} maxWidth="xs" fullWidth
        PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>📢 ¿Cuál es tu consultorio?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#90a4ae', mb: 2 }}>
            Se guardará para esta sesión. Podés cambiarlo cuando quieras.
          </Typography>
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'var(--c-acento)' }}>Consultorio</InputLabel>
            <Select value={consultorioSeleccion} label="Consultorio"
              onChange={e => setConsultorioSeleccion(e.target.value)}
              sx={{ color: '#e0e0e0' }} MenuProps={menuProps}>
              {['Consultorio 1','Consultorio 2','Consultorio 3','Consultorio 4','Consultorio 5'].map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={{ fontSize: 11, color: '#546e7a', mt: 1.5 }}>
            Consultorio actual: <strong style={{ color: '#90a4ae' }}>{sessionStorage.getItem('consultorio') || 'no seteado'}</strong>
            {sessionStorage.getItem('consultorio') && (
              <span style={{ marginLeft: 8, cursor: 'pointer', color: '#ef9a9a' }}
                onClick={() => { sessionStorage.removeItem('consultorio'); setConsultorioSeleccion('Consultorio 1'); }}>
                (cambiar)
              </span>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsultorioModal(false)} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button onClick={confirmarConsultorioYLlamar} variant="contained"
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
            Confirmar y llamar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
